import {
    DeleteOutlined,
    DollarOutlined,
    DownOutlined,
    EditOutlined,
    EuroOutlined,
    GoldOutlined,
    MonitorOutlined,
    PlusOutlined,
    SaveOutlined,
    SyncOutlined,
    UpOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import PostponementApprovalsTab from '../components/PostponementApprovalsTab';
import ControllingTab from '../components/controlling/ControllingTab';
import AuditLogPanel from '../components/controlRoom/AuditLogPanel';
import { useAuth } from '../contexts/AuthContext';
import StatusRefreshAlert from '../components/StatusRefreshAlert';
import BridgeSenderAlert from '../components/BridgeSenderAlert';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiContext';
import {
    Au24KBreakdownRow,
    Au24KData,
    buildControlRoomRefreshState,
    buildFinancialFlowRows,
    DailyBalance,
    DailyCashCheck,
    DailyMovementsGroup,
    DailyStats,
    DeliveredNotInvoiced,
    FinanceGroupData,
    FinanceNote,
    FinancialFlow,
    FinancialItem,
    getDailyCashDifferenceState,
    GrandTotals,
    withCurrency,
} from '../domain/controlRoom';

const { Title, Text } = Typography;

const ControlRoom = () => {
    const { t } = useTranslation();
    const { controlRoom, statuts, configuration, makeRequest } = useApi();
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [loading, setLoading] = useState(true);
    const [statutsLoading, setStatutsLoading] = useState(false);
    const [statutsRunCount, setStatutsRunCount] = useState(0);
    // Each opening of the User Operations tab reloads its list (the pane stays mounted).
    const [auditLogOpenings, setAuditLogOpenings] = useState(0);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [financialItems, setFinancialItems] = useState<FinancialItem[]>([]);
    const [financialFlow, setFinancialFlow] = useState<FinancialFlow>({
        activ: 0,
        pasiv: 0,
        expirat: 0,
        marfa: 0,
    });
    const [grandTotals, setGrandTotals] = useState<GrandTotals>({
        lei: 0,
        euro: 0,
        exchangeRate: 0,
    });
    // Keep AU24K totals; currently only setter is used but we keep both to satisfy lint rules
    const [au24kData, setAu24kData] = useState<Au24KData>({
        grams: 0,
        percentage: 0,
        activTotal: 0,
    });
    useEffect(() => {
        if (import.meta.env.MODE !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('AU24K data updated', au24kData);
        }
    }, [au24kData]);
    const [financeGroupsData, setFinanceGroupsData] = useState<FinanceGroupData[]>([]);
    // Keep AU24K data for potential future use (setter retained)
    const [au24kBreakdown, setAu24kBreakdown] = useState<Au24KBreakdownRow[]>([]);
    const [deliveredNotInvoiced, setDeliveredNotInvoiced] = useState<DeliveredNotInvoiced[]>([]);
    const [financeNotes, setFinanceNotes] = useState<FinanceNote[]>([]);
    const [dailyBalance, setDailyBalance] = useState<DailyBalance[]>([]);
    const [dailyStats, setDailyStats] = useState<DailyStats>({
        venit: 0,
        profit: 0,
        aur585: 0,
        aur750: 0,
        alteCarataje: 0,
        inventarLunar: 0,
        totalComisiiZi: 0,
    });

    // Reference values for index coloring
    const [seeP1, setSeeP1] = useState<number>(0);
    const [seeP2, setSeeP2] = useState<number>(0);
    const [referencesLoading, setReferencesLoading] = useState(false);

    // Row selection state for financial items
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<FinancialItem[]>([]);

    // Sumator line editor state (add / modify / delete)
    const [addLineModalOpen, setAddLineModalOpen] = useState(false);
    const [addLineDenumire, setAddLineDenumire] = useState('');
    const [addLineSuma, setAddLineSuma] = useState<number | null>(null);
    const [addLineReprezentand, setAddLineReprezentand] = useState('');
    const [addLineSaving, setAddLineSaving] = useState(false);

    const [modifyLineModalOpen, setModifyLineModalOpen] = useState(false);
    const [modifyLineDelta, setModifyLineDelta] = useState<number | null>(null);
    const [modifyLineReprezentand, setModifyLineReprezentand] = useState('');
    const [modifyLineSaving, setModifyLineSaving] = useState(false);
    const [deleteLineSaving, setDeleteLineSaving] = useState(false);
    const [financeGroupIndexDrafts, setFinanceGroupIndexDrafts] = useState<Record<number, number>>(
        {},
    );
    const [financeGroupSavingId, setFinanceGroupSavingId] = useState<number | null>(null);
    const [financeNoteDenumire, setFinanceNoteDenumire] = useState('');
    const [financeNoteSuma, setFinanceNoteSuma] = useState<number | null>(null);
    const [financeNoteSaving, setFinanceNoteSaving] = useState(false);
    const [deletingFinanceNoteId, setDeletingFinanceNoteId] = useState<number | null>(null);
    const [deletingDeliveredId, setDeletingDeliveredId] = useState<number | null>(null);
    const [dailyMovements, setDailyMovements] = useState<DailyMovementsGroup[]>([]);
    const [dailyCashCheck, setDailyCashCheck] = useState<DailyCashCheck | null>(null);
    // Authorized operations (BANCA<->SEIF)
    const [bancaToSeifAmount, setBancaToSeifAmount] = useState<number | null>(null);
    const [bancaToSeifRc, setBancaToSeifRc] = useState(true);
    const [seifToBancaAmount, setSeifToBancaAmount] = useState<number | null>(null);
    const [seifToBancaRc, setSeifToBancaRc] = useState(true);
    const [authorizedOpsSaving, setAuthorizedOpsSaving] = useState(false);

    // Load reference values from configuration
    const loadReferenceValues = useCallback(async () => {
        try {
            setReferencesLoading(true);
            const [indexP1Response, indexP2Response] = await Promise.all([
                configuration.get('IndexP1'),
                configuration.get('IndexP2'),
            ]);

            if (indexP1Response.success && indexP1Response.data) {
                setSeeP1(parseFloat(indexP1Response.data.valoare) || 0);
            } else {
                setSeeP1(0);
            }
            if (indexP2Response.success && indexP2Response.data) {
                setSeeP2(parseFloat(indexP2Response.data.valoare) || 0);
            } else {
                setSeeP2(0);
            }
        } catch (error) {
            console.error('Failed to load reference values:', error);
        } finally {
            setReferencesLoading(false);
        }
    }, [configuration]);

    // Save reference values to configuration
    const saveReferenceValues = useCallback(
        async (newSeeP1: number, newSeeP2: number) => {
            try {
                setReferencesLoading(true);
                await configuration.updateBatch([
                    { denumire: 'IndexP1', valoare: newSeeP1.toString() },
                    { denumire: 'IndexP2', valoare: newSeeP2.toString() },
                ]);
                setSeeP1(newSeeP1);
                setSeeP2(newSeeP2);
                message.success(t('controlRoom.referenceValuesSaved', 'Reference values saved'));
            } catch (error) {
                console.error('Failed to save reference values:', error);
                message.error(
                    t('controlRoom.referenceValuesSaveError', 'Failed to save reference values'),
                );
            } finally {
                setReferencesLoading(false);
            }
        },
        [configuration, t],
    );

    // Determine row color using Delphi-style logic:
    // color red  -> Valoare <= Index_baza * (seeP1 / 100)
    // color yellow -> Valoare > Index_baza * (seeP1 / 100)
    // color green -> Valoare > Index_baza * (seeP2 / 100)
    // When seeP1 and seeP2 are both zero (not configured) or indexBaza is falsy, return white.
    const getRowColor = useCallback(
        (valoare: number, indexBaza: number) => {
            if ((seeP1 === 0 && seeP2 === 0) || !indexBaza) return '#ffffff'; // Default white if no reference values or invalid base

            const threshold1 = (indexBaza ?? 0) * ((seeP1 ?? 0) / 100);
            const threshold2 = (indexBaza ?? 0) * ((seeP2 ?? 0) / 100);

            // Follow the same overriding order as the original Delphi code: later checks override earlier ones.
            // Final intended semantics: green if > threshold2, yellow if > threshold1, red otherwise (<= threshold1).
            if (valoare > threshold2) return '#52c41a'; // green
            if (valoare > threshold1) return '#FFEA00'; // yellow
            return '#ff4d4f'; // red
        },
        [seeP1, seeP2],
    );

    // SetStatuts execution function
    const executeSetStatuts = useCallback(async () => {
        try {
            setStatutsLoading(true);
            message.loading({
                content: t('controlRoom.executingSetStatuts', 'Executing SetStatuts...'),
                key: 'setstatuts',
            });

            const result = await statuts.execute();

            if (result.success && result.data) {
                message.success({
                    content: `${t('controlRoom.setStatutsSuccess', 'SetStatuts executed successfully')} (${result.data.executionTime})`,
                    key: 'setstatuts',
                });
                // Optionally refresh data after execution
                // refreshData(); // Will be called manually if needed
            } else {
                message.error({
                    content: t('controlRoom.setStatutsError', 'Failed to execute SetStatuts'),
                    key: 'setstatuts',
                });
            }
        } catch (error) {
            console.error('SetStatuts execution failed:', error);
            message.error({
                content: t('controlRoom.setStatutsError', 'Failed to execute SetStatuts'),
                key: 'setstatuts',
            });
        } finally {
            setStatutsLoading(false);
            setStatutsRunCount(count => count + 1);
        }
    }, [statuts, t]);

    const refreshData = useCallback(
        async (date: string) => {
            try {
                setLoading(true);
                const selectedDateStr = date;

                // Load all data in parallel for better performance
                const [
                    generalDataResponse,
                    dailyBalanceResponse,
                    dailyStatsResponse,
                    financeGroupsResponse,
                    financialItemsResponse,
                    au24kBreakdownResponse,
                    dailyMovementsResponse,
                    dailyCashCheckResponse,
                ] = await Promise.all([
                    controlRoom.getAll(selectedDateStr),
                    controlRoom.getDailyBalance(selectedDateStr),
                    controlRoom.getDailyStats(selectedDateStr),
                    controlRoom.getFinanceGroups(selectedDateStr),
                    controlRoom.getFinancialItems(selectedDateStr),
                    controlRoom.getAu24kBreakdown(selectedDateStr),
                    controlRoom.getDailyMovements(selectedDateStr),
                    controlRoom.getDailyCashCheck(selectedDateStr),
                ]);

                // General data with graceful fallbacks
                const g = generalDataResponse.success ? generalDataResponse.data : undefined;
                setFinancialFlow(g?.financialFlow ?? { activ: 0, pasiv: 0, expirat: 0, marfa: 0 });
                setGrandTotals(g?.grandTotals ?? { lei: 0, euro: 0, exchangeRate: 0 });
                setAu24kData(g?.au24kData ?? { grams: 0, percentage: 0, activTotal: 0 });
                setFinanceGroupsData(g?.financeGroupsData ?? []);
                const maybeAny = g as unknown as Record<string, unknown> | undefined;
                if (maybeAny && Array.isArray(maybeAny['au24kBreakdown'])) {
                    setAu24kBreakdown(
                        maybeAny['au24kBreakdown'] as Array<{
                            category: string;
                            GR: number;
                            RON: number;
                        }>,
                    );
                }
                setDeliveredNotInvoiced(g?.deliveredNotInvoiced ?? []);
                setFinanceNotes(g?.financeNotes ?? []);

                // Prefer dedicated finance groups endpoint when available
                if (financeGroupsResponse.success && financeGroupsResponse.data) {
                    setFinanceGroupsData(financeGroupsResponse.data);
                    setFinanceGroupIndexDrafts({});
                }

                // Prefer dedicated au24k breakdown endpoint (supports historical dates)
                if (au24kBreakdownResponse.success && Array.isArray(au24kBreakdownResponse.data)) {
                    setAu24kBreakdown(au24kBreakdownResponse.data);
                }

                const {
                    financialItems: nextFinancialItems,
                    dailyBalance: nextDailyBalance,
                    dailyStats: nextDailyStats,
                    dailyMovements: nextDailyMovements,
                    dailyCashCheck: nextDailyCashCheck,
                } = buildControlRoomRefreshState({
                    selectedDateStr,
                    fallbackDailyBalanceLabel: t('controlRoom.mockData.friday', 'Vineri'),
                    financialItemsResponse,
                    dailyBalanceResponse,
                    dailyStatsResponse,
                    dailyMovementsResponse,
                    dailyCashCheckResponse,
                });

                setFinancialItems(nextFinancialItems);
                setDailyBalance(nextDailyBalance);
                setDailyStats(nextDailyStats);
                setDailyMovements(nextDailyMovements);
                setDailyCashCheck(nextDailyCashCheck);
            } catch (error) {
                console.error('Failed to refresh control room data:', error);
            } finally {
                setLoading(false);
            }
        },
        [controlRoom, t],
    );

    useEffect(() => {
        // Load data only once on initial mount with current date
        refreshData(dayjs().format('YYYY-MM-DD'));
        // Load reference values for index coloring
        loadReferenceValues();
    }, [refreshData, controlRoom, loadReferenceValues]);

    const handleDateChange = useCallback(
        (date: dayjs.Dayjs | null) => {
            const newDate = date || dayjs();
            setSelectedDate(newDate);
            refreshData(newDate.format('YYYY-MM-DD'));
        },
        [refreshData],
    );

    const handleSummedToggle = useCallback(
        async (id: number, checked: boolean) => {
            try {
                // Update the local state immediately for better UX
                setFinancialItems(prevItems =>
                    prevItems.map(item => (item.id === id ? { ...item, isSumed: checked } : item)),
                );

                // Call the API to update the backend
                const result = await controlRoom.updateFinancialItemSummed(id, checked);

                if (!result.success) {
                    // Revert the local state if the API call failed
                    setFinancialItems(prevItems =>
                        prevItems.map(item =>
                            item.id === id ? { ...item, isSumed: !checked } : item,
                        ),
                    );
                    message.error(
                        t('controlRoom.updateSummedError', 'Failed to update summed status'),
                    );
                } else {
                    // Refresh the total calculation
                    message.success(t('controlRoom.updateSummedSuccess', 'Summed status updated'));
                }
            } catch (error) {
                console.error('Failed to update summed status:', error);
                // Revert the local state on error
                setFinancialItems(prevItems =>
                    prevItems.map(item => (item.id === id ? { ...item, isSumed: !checked } : item)),
                );
                message.error(t('controlRoom.updateSummedError', 'Failed to update summed status'));
            }
        },
        [controlRoom, t],
    );

    // Row selection handlers
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: FinancialItem[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
            setSelectedRows(newSelectedRows);
        },
        type: 'radio' as const, // Only allow single selection
    };

    const saveReorderedItems = useCallback(
        async (
            reordered: FinancialItem[],
            rollback: (prev: FinancialItem[]) => void,
            logLabel: string,
        ) => {
            const prevItems = [...financialItems];
            const reassigned = reordered.map((item, idx) => ({
                ...item,
                ordern: idx + 1,
            }));
            rollback(reassigned);

            try {
                const orderUpdates = reassigned.map(item => ({
                    id: item.id,
                    ordern: item.ordern,
                }));
                const result = await controlRoom.updateFinancialItemOrder(orderUpdates);

                if (!result.success) {
                    rollback(prevItems);
                    message.error(t('controlRoom.updateOrderError', 'Failed to update row order'));
                }
            } catch (error) {
                console.error(`Failed to ${logLabel}:`, error);
                rollback(prevItems);
                message.error(t('controlRoom.updateOrderError', 'Failed to update row order'));
            }
        },
        [financialItems, controlRoom, t],
    );

    // Move selected row up
    const moveRowUp = useCallback(async () => {
        if (selectedRowKeys.length !== 1) {
            message.warning(t('controlRoom.selectSingleRow', 'Please select a single row'));
            return;
        }

        const selectedId = selectedRowKeys[0] as number;
        const currentIndex = financialItems.findIndex(item => item.id === selectedId);

        if (currentIndex <= 0) {
            message.warning(t('controlRoom.cannotMoveUp', 'Cannot move up - already at the top'));
            return;
        }

        const newItems = [...financialItems];
        const [movedItem] = newItems.splice(currentIndex, 1);
        newItems.splice(currentIndex - 1, 0, movedItem);

        await saveReorderedItems(newItems, prev => setFinancialItems(prev), 'moveRowUp');
    }, [selectedRowKeys, financialItems, t, saveReorderedItems]);

    // Move selected row down
    const moveRowDown = useCallback(async () => {
        if (selectedRowKeys.length !== 1) {
            message.warning(t('controlRoom.selectSingleRow', 'Please select a single row'));
            return;
        }

        const selectedId = selectedRowKeys[0] as number;
        const currentIndex = financialItems.findIndex(item => item.id === selectedId);

        if (currentIndex >= financialItems.length - 1) {
            message.warning(
                t('controlRoom.cannotMoveDown', 'Cannot move down - already at the bottom'),
            );
            return;
        }

        const newItems = [...financialItems];
        const [movedItem] = newItems.splice(currentIndex, 1);
        newItems.splice(currentIndex + 1, 0, movedItem);

        await saveReorderedItems(newItems, prev => setFinancialItems(prev), 'moveRowDown');
    }, [selectedRowKeys, financialItems, t, saveReorderedItems]);

    // Open the "Add Line" modal (Sumator "Adaugare rand")
    const openAddLineModal = useCallback(() => {
        setAddLineDenumire('');
        setAddLineSuma(null);
        setAddLineReprezentand('');
        setAddLineModalOpen(true);
    }, []);

    const handleAddLine = useCallback(async () => {
        if (!addLineDenumire.trim()) {
            message.warning(t('controlRoom.lineNameRequired', 'Line name is required'));
            return;
        }
        if (addLineSuma === null || addLineSuma === 0) {
            message.warning(t('controlRoom.amountZeroError', 'Amount cannot be zero'));
            return;
        }
        if (!addLineReprezentand.trim()) {
            message.warning(t('controlRoom.reasonRequired', 'Reason is required'));
            return;
        }

        try {
            setAddLineSaving(true);
            const result = await controlRoom.addFinancialItem({
                denumire: addLineDenumire.trim(),
                suma: addLineSuma,
                reprezentand: addLineReprezentand.trim(),
            });

            if (result.success) {
                message.success(t('controlRoom.addLineSuccess', 'Line added successfully'));
                setAddLineModalOpen(false);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                message.error(t('controlRoom.addLineError', 'Failed to add line'));
            }
        } catch (error) {
            console.error('Failed to add financial line:', error);
            message.error(t('controlRoom.addLineError', 'Failed to add line'));
        } finally {
            setAddLineSaving(false);
        }
    }, [
        addLineDenumire,
        addLineSuma,
        addLineReprezentand,
        controlRoom,
        t,
        refreshData,
        selectedDate,
    ]);

    // Open the "Modify Line" modal (Sumator "Modifica") — disallowed for automated lines
    const openModifyLineModal = useCallback(() => {
        if (selectedRows.length !== 1) {
            message.warning(t('controlRoom.selectSingleRow', 'Please select a single row'));
            return;
        }
        if (selectedRows[0].automated) {
            message.warning(
                t('controlRoom.automatedLineCannotModify', 'Automated lines cannot be modified'),
            );
            return;
        }
        setModifyLineDelta(null);
        setModifyLineReprezentand('');
        setModifyLineModalOpen(true);
    }, [selectedRows, t]);

    const handleModifyLine = useCallback(async () => {
        if (selectedRows.length !== 1) return;
        if (modifyLineDelta === null || modifyLineDelta === 0) {
            message.warning(t('controlRoom.amountZeroError', 'Amount cannot be zero'));
            return;
        }
        if (!modifyLineReprezentand.trim()) {
            message.warning(t('controlRoom.reasonRequired', 'Reason is required'));
            return;
        }

        try {
            setModifyLineSaving(true);
            const result = await controlRoom.modifyFinancialItem(selectedRows[0].id, {
                delta: modifyLineDelta,
                reprezentand: modifyLineReprezentand.trim(),
            });

            if (result.success) {
                message.success(t('controlRoom.modifyLineSuccess', 'Line modified successfully'));
                setModifyLineModalOpen(false);
                setSelectedRowKeys([]);
                setSelectedRows([]);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                message.error(t('controlRoom.modifyLineError', 'Failed to modify line'));
            }
        } catch (error) {
            console.error('Failed to modify financial line:', error);
            message.error(t('controlRoom.modifyLineError', 'Failed to modify line'));
        } finally {
            setModifyLineSaving(false);
        }
    }, [
        selectedRows,
        modifyLineDelta,
        modifyLineReprezentand,
        controlRoom,
        t,
        refreshData,
        selectedDate,
    ]);

    // Delete the selected line (Sumator "Stergere rand") — disallowed for fixed lines
    const handleDeleteLine = useCallback(async () => {
        if (selectedRows.length !== 1) {
            message.warning(t('controlRoom.selectSingleRow', 'Please select a single row'));
            return;
        }
        if (selectedRows[0].fixed) {
            message.warning(
                t('controlRoom.fixedLineCannotDelete', 'Fixed lines cannot be deleted'),
            );
            return;
        }

        try {
            setDeleteLineSaving(true);
            const result = await controlRoom.deleteFinancialItem(selectedRows[0].id);

            if (result.success) {
                message.success(t('controlRoom.deleteLineSuccess', 'Line deleted successfully'));
                setSelectedRowKeys([]);
                setSelectedRows([]);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                message.error(t('controlRoom.deleteLineError', 'Failed to delete line'));
            }
        } catch (error) {
            console.error('Failed to delete financial line:', error);
            message.error(t('controlRoom.deleteLineError', 'Failed to delete line'));
        } finally {
            setDeleteLineSaving(false);
        }
    }, [selectedRows, controlRoom, t, refreshData, selectedDate]);

    const handleSaveFinanceGroupIndex = useCallback(
        async (group: FinanceGroupData) => {
            const draftValue = financeGroupIndexDrafts[group.id] ?? group.indexBaza ?? 0;
            if (draftValue < 0) {
                message.warning(
                    t('controlRoom.indexBaseNonNegative', 'Index base must be zero or greater'),
                );
                return;
            }

            try {
                setFinanceGroupSavingId(group.id);
                const result = await controlRoom.updateFinanceGroupIndex(group.id, draftValue);
                if (result.success) {
                    message.success(t('controlRoom.indexBaseSaveSuccess', 'Index saved'));
                    await refreshData(selectedDate.format('YYYY-MM-DD'));
                } else {
                    message.error(t('controlRoom.indexBaseSaveError', 'Failed to save index'));
                }
            } catch (error) {
                console.error('Failed to save finance group index:', error);
                message.error(t('controlRoom.indexBaseSaveError', 'Failed to save index'));
            } finally {
                setFinanceGroupSavingId(null);
            }
        },
        [controlRoom, financeGroupIndexDrafts, refreshData, selectedDate, t],
    );

    const handleAddFinanceNote = useCallback(async () => {
        if (!financeNoteDenumire.trim()) {
            message.warning(
                t('controlRoom.noteDescriptionRequired', 'Note description is required'),
            );
            return;
        }
        if (financeNoteSuma === null || !Number.isFinite(financeNoteSuma)) {
            message.warning(t('controlRoom.amountRequired', 'Amount is required'));
            return;
        }

        try {
            setFinanceNoteSaving(true);
            const result = await controlRoom.addFinanceNote({
                denumire: financeNoteDenumire.trim(),
                suma: financeNoteSuma,
            });

            if (result.success) {
                message.success(t('controlRoom.noteAddSuccess', 'Note added successfully'));
                setFinanceNoteDenumire('');
                setFinanceNoteSuma(null);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                message.error(t('controlRoom.noteAddError', 'Failed to add note'));
            }
        } catch (error) {
            console.error('Failed to add finance note:', error);
            message.error(t('controlRoom.noteAddError', 'Failed to add note'));
        } finally {
            setFinanceNoteSaving(false);
        }
    }, [controlRoom, financeNoteDenumire, financeNoteSuma, refreshData, selectedDate, t]);

    const handleDeleteFinanceNote = useCallback(
        async (id: number) => {
            try {
                setDeletingFinanceNoteId(id);
                const result = await controlRoom.deleteFinanceNote(id);
                if (result.success) {
                    message.success(
                        t('controlRoom.noteDeleteSuccess', 'Note deleted successfully'),
                    );
                    await refreshData(selectedDate.format('YYYY-MM-DD'));
                } else {
                    message.error(t('controlRoom.noteDeleteError', 'Failed to delete note'));
                }
            } catch (error) {
                console.error('Failed to delete finance note:', error);
                message.error(t('controlRoom.noteDeleteError', 'Failed to delete note'));
            } finally {
                setDeletingFinanceNoteId(null);
            }
        },
        [controlRoom, refreshData, selectedDate, t],
    );

    const handleDeleteDeliveredNotInvoiced = useCallback(
        async (id: number) => {
            try {
                setDeletingDeliveredId(id);
                const result = await controlRoom.deleteDeliveredNotInvoiced(id);
                if (result.success) {
                    message.success(
                        t(
                            'controlRoom.deliveredDeleteSuccess',
                            'Delivered row deleted successfully',
                        ),
                    );
                    await refreshData(selectedDate.format('YYYY-MM-DD'));
                } else {
                    message.error(
                        t('controlRoom.deliveredDeleteError', 'Failed to delete delivered row'),
                    );
                }
            } catch (error) {
                console.error('Failed to delete delivered-not-invoiced row:', error);
                message.error(
                    t('controlRoom.deliveredDeleteError', 'Failed to delete delivered row'),
                );
            } finally {
                setDeletingDeliveredId(null);
            }
        },
        [controlRoom, refreshData, selectedDate, t],
    );

    const handleBancaToSeif = useCallback(async () => {
        if (!bancaToSeifAmount || bancaToSeifAmount <= 0) {
            void message.warning(t('controlRoom.authorizedOps.amountRequired'));
            return;
        }
        setAuthorizedOpsSaving(true);
        try {
            const res = await makeRequest<{ success: boolean }>('/dispozitii/banca-to-seif', {
                method: 'POST',
                body: JSON.stringify({
                    amount: bancaToSeifAmount,
                    rc: bancaToSeifRc,
                }),
            });
            if (res.success) {
                void message.success(t('controlRoom.authorizedOps.success'));
                setBancaToSeifAmount(null);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                void message.error(res.error ?? t('controlRoom.authorizedOps.error'));
            }
        } catch {
            void message.error(t('controlRoom.authorizedOps.error'));
        } finally {
            setAuthorizedOpsSaving(false);
        }
    }, [bancaToSeifAmount, bancaToSeifRc, makeRequest, refreshData, selectedDate, t]);

    const handleSeifToBanca = useCallback(async () => {
        if (!seifToBancaAmount || seifToBancaAmount <= 0) {
            void message.warning(t('controlRoom.authorizedOps.amountRequired'));
            return;
        }
        setAuthorizedOpsSaving(true);
        try {
            const res = await makeRequest<{ success: boolean }>('/dispozitii/seif-to-banca', {
                method: 'POST',
                body: JSON.stringify({
                    amount: seifToBancaAmount,
                    rc: seifToBancaRc,
                }),
            });
            if (res.success) {
                void message.success(t('controlRoom.authorizedOps.success'));
                setSeifToBancaAmount(null);
                await refreshData(selectedDate.format('YYYY-MM-DD'));
            } else {
                void message.error(res.error ?? t('controlRoom.authorizedOps.error'));
            }
        } catch {
            void message.error(t('controlRoom.authorizedOps.error'));
        } finally {
            setAuthorizedOpsSaving(false);
        }
    }, [seifToBancaAmount, seifToBancaRc, makeRequest, refreshData, selectedDate, t]);

    const sumatorColumns = [
        {
            title: t('controlRoom.description', 'Description'),
            dataIndex: 'denumire',
            key: 'denumire',
            width: 140,
        },
        {
            title: t('controlRoom.amount', 'Amount'),
            dataIndex: 'suma',
            key: 'suma',
            width: 90,
            render: (value: number) =>
                new Intl.NumberFormat('ro-RO', {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                }).format(Math.round(value ?? 0)),
        },
        {
            title: t('controlRoom.date', 'Date'),
            dataIndex: 'data',
            key: 'data',
            width: 85,
            render: (value: string) => {
                if (!value) return '';
                // preserve placeholder values like '-'
                if (value === '-') return value;
                const d = dayjs(value);
                return d.isValid() ? d.format('YYYY-MM-DD') : value;
            },
        },
        {
            title: t('controlRoom.savedAt', 'Saved At'),
            dataIndex: 'stamp',
            key: 'stamp',
            width: 110,
            render: (value: string) => new Date(value).toLocaleString('ro-RO'),
        },
        {
            title: t('controlRoom.summed', 'Summed'),
            dataIndex: 'isSumed',
            key: 'isSumed',
            width: 60,
            render: (value: boolean, record: FinancialItem) => (
                <input
                    type="checkbox"
                    checked={value}
                    onChange={e => handleSummedToggle(record.id, e.target.checked)}
                />
            ),
        },
    ];

    const deliveredNotInvoicedColumns = [
        {
            title: t('controlRoom.contractNr', 'Contract Nr'),
            dataIndex: 'nrContract',
            key: 'nrContract',
        },
        {
            title: t('controlRoom.name', 'Name'),
            dataIndex: 'nume',
            key: 'nume',
            width: 150,
        },
        {
            title: t('controlRoom.object', 'Object'),
            dataIndex: 'obiect',
            key: 'obiect',
            width: 200,
        },
        {
            title: t('controlRoom.date', 'Date'),
            dataIndex: 'data',
            key: 'data',
        },
        {
            title: t('controlRoom.actions', 'Actions'),
            key: 'actions',
            width: 80,
            render: (_: unknown, record: DeliveredNotInvoiced) => (
                <Popconfirm
                    title={t(
                        'controlRoom.confirmDeleteDelivered',
                        'Are you sure you want to delete this delivered row?',
                    )}
                    onConfirm={() => handleDeleteDeliveredNotInvoiced(record.id)}
                >
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingDeliveredId === record.id}
                        title={t('controlRoom.deleteDelivered', 'Delete delivered row')}
                    />
                </Popconfirm>
            ),
        },
    ];

    const notesColumns = [
        {
            title: t('controlRoom.description', 'Description'),
            dataIndex: 'denumire',
            key: 'denumire',
            width: 200,
        },
        {
            title: t('controlRoom.amount', 'Amount'),
            dataIndex: 'suma',
            key: 'suma',
            width: 120,
            render: (value: number) => (
                <span style={{ color: value < 0 ? 'red' : 'green' }}>
                    {new Intl.NumberFormat('ro-RO', {
                        signDisplay: 'always',
                        maximumFractionDigits: 0,
                        minimumFractionDigits: 0,
                    }).format(Math.round(value ?? 0))}
                </span>
            ),
        },
        {
            title: t('controlRoom.date', 'Date'),
            dataIndex: 'data',
            key: 'data',
        },
        {
            title: t('controlRoom.actions', 'Actions'),
            key: 'actions',
            width: 80,
            render: (_: unknown, record: FinanceNote) => (
                <Popconfirm
                    title={t(
                        'controlRoom.confirmDeleteNote',
                        'Are you sure you want to delete this note?',
                    )}
                    onConfirm={() => handleDeleteFinanceNote(record.id)}
                >
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingFinanceNoteId === record.id}
                        title={t('controlRoom.deleteNote', 'Delete note')}
                    />
                </Popconfirm>
            ),
        },
    ];

    const format0 = (num?: number | null): string =>
        new Intl.NumberFormat('ro-RO', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(Math.round(num ?? 0));

    const render0 = (value: number): string => format0(value);

    const dailyBalanceColumns = [
        {
            title: t('controlRoom.day', 'Day'),
            dataIndex: 'ziua',
            key: 'ziua',
            width: 60,
        },
        {
            title: t('controlRoom.date', 'Date'),
            dataIndex: 'data',
            key: 'data',
            width: 80,
            render: (value: string) => {
                if (!value) return '';
                // Ensure date is formatted as yyyy-mm-dd without time
                const date = new Date(value);
                return date.toISOString().split('T')[0];
            },
        },
        {
            title: t('controlRoom.contractsCount', 'Contracts Count'),
            dataIndex: 'nrContracte',
            key: 'nrContracte',
            width: 70,
        },
        {
            title: t('controlRoom.contractsValue', 'Contracts Value'),
            dataIndex: 'valContracte',
            key: 'valContracte',
            width: 80,
            render: render0,
        },
        {
            title: t('controlRoom.paidContractsCount', 'Paid Contracts Count'),
            dataIndex: 'nrContractePlatite',
            key: 'nrContractePlatite',
            width: 90,
        },
        {
            title: t('controlRoom.paidContractsValue', 'Paid Contracts Value'),
            dataIndex: 'valContractePlatite',
            key: 'valContractePlatite',
            width: 100,
            render: render0,
        },
        {
            title: t('controlRoom.paidContractsCommission', 'Paid Contracts Commission'),
            dataIndex: 'comContractePlatite',
            key: 'comContractePlatite',
            width: 110,
            render: render0,
        },
        {
            title: t('controlRoom.paidCommissionsCount', 'Paid Commissions Count'),
            dataIndex: 'nrComisioanePlatite',
            key: 'nrComisioanePlatite',
            width: 90,
        },
        {
            title: t('controlRoom.paidCommissionsValue', 'Paid Commissions Value'),
            dataIndex: 'valComisioanePlatite',
            key: 'valComisioanePlatite',
            width: 100,
            render: render0,
        },
    ];

    const totalSumator = financialItems
        .filter(item => item.isSumed)
        .reduce((sum, item) => sum + item.suma, 0);
    const financialFlowRows = buildFinancialFlowRows(financialFlow, grandTotals.exchangeRate);
    const dailyCashDifferenceState = getDailyCashDifferenceState(
        dailyCashCheck ? dailyCashCheck.difference : 0,
    );
    const dailyCashDifferenceColors = {
        balanced: 'green',
        surplus: 'blue',
        shortage: 'red',
        notCounted: 'default',
    } as const;
    /** A till nobody counted shows a dash, not a count of zero. */
    const formatCounted = (value: number | null | undefined): string =>
        value === null ? '-' : format0(value);
    const dailyCashDifferenceColor = dailyCashDifferenceColors[dailyCashDifferenceState];
    const dailyCashBreakdown = dailyCashCheck
        ? [...dailyCashCheck.intrari, ...dailyCashCheck.iesiri]
        : [];

    const dailyMovementsGroupColumns = [
        {
            title: t('controlRoom.dailyMovements.type', 'Type'),
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (type: string) => (
                <Tag color={type === 'intrari' ? 'green' : 'red'}>
                    {t(`controlRoom.dailyMovements.${type}`, type)}
                </Tag>
            ),
        },
        {
            title: t('controlRoom.dailyMovements.group', 'Group'),
            dataIndex: 'group',
            key: 'group',
            render: (group: string) => t(`controlRoom.dailyMovements.group_${group}`, group),
        },
        {
            title: t('controlRoom.dailyMovements.entriesCount', 'Entries'),
            key: 'entriesCount',
            width: 80,
            render: (_: unknown, record: DailyMovementsGroup) => record.entries.length,
        },
        {
            title: t('controlRoom.amount', 'Amount'),
            dataIndex: 'total',
            key: 'total',
            width: 120,
            render: (total: number) => format0(total),
        },
    ];

    const renderMovementsEntryTable = useCallback(
        (record: DailyMovementsGroup) => (
            <Table
                dataSource={record.entries}
                rowKey="id"
                rowClassName="daily-movements-entry-row"
                columns={[
                    {
                        title: t('controlRoom.description', 'Description'),
                        dataIndex: 'label',
                        key: 'label',
                    },
                    {
                        title: t('controlRoom.amount', 'Amount'),
                        dataIndex: 'amount',
                        key: 'amount',
                        width: 100,
                        render: (val: number) =>
                            new Intl.NumberFormat('ro-RO', {
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0,
                            }).format(Math.round(val ?? 0)),
                    },
                    {
                        title: t('controlRoom.dailyMovements.user', 'User'),
                        dataIndex: 'user',
                        key: 'user',
                        width: 80,
                        render: (val?: string) => val ?? '-',
                    },
                    {
                        title: t('controlRoom.dailyMovements.time', 'Time'),
                        dataIndex: 'timestamp',
                        key: 'timestamp',
                        width: 90,
                        render: (val?: string) => val ?? '-',
                    },
                    {
                        title: t('controlRoom.dailyMovements.invoiceRef', 'Ref'),
                        dataIndex: 'invoiceRef',
                        key: 'invoiceRef',
                        width: 100,
                        render: (val?: string) => val ?? '-',
                    },
                ]}
                size="small"
                pagination={false}
            />
        ),
        [t],
    );

    const sumatorSummary = useCallback(
        () => (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>
                    <Text strong>{t('controlRoom.total', 'TOTAL')}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                    <Text strong>
                        {new Intl.NumberFormat('ro-RO', {
                            maximumFractionDigits: 0,
                            minimumFractionDigits: 0,
                        }).format(Math.round(totalSumator ?? 0))}
                    </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
            </Table.Summary.Row>
        ),
        [t, totalSumator],
    );

    // Delphi parity (udispozitii.pas): when historical data is reconstructed via live
    // calculation (no snapshot in stat_index / stat_au24k), the grid is shown with a
    // silver background to signal the values are calculated rather than a stored snapshot.
    const indexCalculated = financeGroupsData[0]?.source === 'calculated';
    const au24kCalculated = au24kBreakdown[0]?.source === 'calculated';
    const calculatedBackground = '#c0c0c0';

    return (
        <div>
            <div
                className="page-header"
                style={{ '--page-header-accent': '#0369a1' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <MonitorOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('controlRoom.title', 'Control Room')}
                        </Title>
                        <Text type="secondary">
                            {t(
                                'controlRoom.subtitle',
                                'System monitoring and administration panel',
                            )}
                        </Text>
                    </div>
                </div>
                <div className="page-header__actions">
                    <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={statutsLoading}
                        onClick={executeSetStatuts}
                        size="large"
                    >
                        {t('controlRoom.setStatuts', 'Update Statuts')}
                    </Button>
                </div>
            </div>

            <StatusRefreshAlert refreshToken={statutsRunCount} />
            <BridgeSenderAlert />

            <Tabs
                defaultActiveKey="dashboard"
                onChange={key => {
                    if (key === 'auditLog') {
                        setAuditLogOpenings(count => count + 1);
                    }
                }}
                items={[
                    {
                        key: 'dashboard',
                        label: t('controlRoom.tabs.dashboard', 'Dashboard'),
                        children: (
                            <>
                                {/* Daily Balance Section */}
                                <Card
                                    title={
                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <span>
                                                    {withCurrency(
                                                        t(
                                                            'controlRoom.dailyBalance',
                                                            'Daily Balance',
                                                        ),
                                                        'EUR',
                                                    )}
                                                </span>
                                            </Col>
                                            <Col>
                                                <DatePicker
                                                    value={selectedDate}
                                                    onChange={handleDateChange}
                                                    format="YYYY-MM-DD"
                                                    allowClear={false}
                                                />
                                            </Col>
                                        </Row>
                                    }
                                    loading={loading}
                                    style={{ marginBottom: 24 }}
                                >
                                    {/* Daily Balance Grid */}
                                    <Table
                                        dataSource={dailyBalance}
                                        columns={dailyBalanceColumns}
                                        size="small"
                                        pagination={false}
                                        scroll={{ x: 800 }}
                                        style={{ marginBottom: 16 }}
                                        rowKey={record => `${record.data}-${record.ziua}`}
                                    />

                                    {/* Daily Stats - moved below the table */}
                                    <Card
                                        size="small"
                                        title={withCurrency(
                                            t('controlRoom.dailyStats', 'Daily Statistics'),
                                            'EUR',
                                        )}
                                    >
                                        <Row gutter={[16, 8]}>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>{t('controlRoom.revenue', 'Revenue')}: </Text>
                                                <Text strong>
                                                    {new Intl.NumberFormat('ro-RO', {
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                    }).format(Math.round(dailyStats.venit ?? 0))}
                                                </Text>
                                            </Col>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>{t('controlRoom.profit', 'Profit')}: </Text>
                                                <Text strong>
                                                    {new Intl.NumberFormat('ro-RO', {
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                    }).format(Math.round(dailyStats.profit ?? 0))}
                                                </Text>
                                            </Col>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>
                                                    {t('controlRoom.gold585', 'Gold 585')}:{' '}
                                                </Text>
                                                <Text strong>
                                                    {Math.round(dailyStats.aur585 ?? 0)}g
                                                </Text>
                                            </Col>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>
                                                    {t('controlRoom.gold750', 'Gold 750')}:{' '}
                                                </Text>
                                                <Text strong>
                                                    {Math.round(dailyStats.aur750 ?? 0)}g
                                                </Text>
                                            </Col>
                                        </Row>
                                        <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>
                                                    {t('controlRoom.otherCarats', 'Other Carats')}
                                                    :{' '}
                                                </Text>
                                                <Text strong>
                                                    {Math.round(dailyStats.alteCarataje ?? 0)}g
                                                </Text>
                                            </Col>
                                            <Col xs={12} sm={8} md={6}>
                                                <Text>
                                                    {t(
                                                        'controlRoom.monthlyInventory',
                                                        'Monthly Inventory',
                                                    )}
                                                    :{' '}
                                                </Text>
                                                <Text strong>
                                                    {new Intl.NumberFormat('ro-RO', {
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                    }).format(
                                                        Math.round(dailyStats.inventarLunar ?? 0),
                                                    )}
                                                </Text>
                                            </Col>
                                            <Col xs={24} sm={16} md={12}>
                                                <Text strong style={{ fontSize: '14px' }}>
                                                    {t(
                                                        'controlRoom.totalDailyCommissions',
                                                        'Total Daily Commissions',
                                                    )}
                                                    :{' '}
                                                    {new Intl.NumberFormat('ro-RO', {
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                    }).format(
                                                        Math.round(dailyStats.totalComisiiZi ?? 0),
                                                    )}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Card>

                                {/* Main Cards Row */}
                                <Row gutter={[16, 16]}>
                                    {/* Left Column - SUMATOR */}
                                    <Col xs={24} lg={8}>
                                        <Card
                                            title={
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Text strong style={{ fontSize: '16px' }}>
                                                            {withCurrency(
                                                                t('controlRoom.sumator', 'SUMATOR'),
                                                                'LEI',
                                                            )}
                                                        </Text>
                                                    </Col>
                                                    <Col>
                                                        <Space>
                                                            <Button
                                                                size="small"
                                                                icon={<PlusOutlined />}
                                                                onClick={openAddLineModal}
                                                                disabled={loading}
                                                                title={t(
                                                                    'controlRoom.addLine',
                                                                    'Add Line',
                                                                )}
                                                            />
                                                            <Button
                                                                size="small"
                                                                icon={<EditOutlined />}
                                                                onClick={openModifyLineModal}
                                                                disabled={
                                                                    selectedRows.length !== 1 ||
                                                                    selectedRows[0]?.automated ||
                                                                    loading
                                                                }
                                                                title={t(
                                                                    'controlRoom.modifyLine',
                                                                    'Modify Line',
                                                                )}
                                                            />
                                                            <Popconfirm
                                                                title={t(
                                                                    'controlRoom.confirmDeleteLine',
                                                                    'Are you sure you want to delete this line?',
                                                                )}
                                                                onConfirm={handleDeleteLine}
                                                                disabled={
                                                                    selectedRows.length !== 1 ||
                                                                    selectedRows[0]?.fixed ||
                                                                    loading
                                                                }
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    loading={deleteLineSaving}
                                                                    disabled={
                                                                        selectedRows.length !== 1 ||
                                                                        selectedRows[0]?.fixed ||
                                                                        loading
                                                                    }
                                                                    title={t(
                                                                        'controlRoom.deleteLine',
                                                                        'Delete Line',
                                                                    )}
                                                                />
                                                            </Popconfirm>
                                                            <Button
                                                                size="small"
                                                                icon={<UpOutlined />}
                                                                onClick={moveRowUp}
                                                                disabled={
                                                                    selectedRows.length !== 1 ||
                                                                    loading
                                                                }
                                                                title={t(
                                                                    'controlRoom.moveUp',
                                                                    'Move Up',
                                                                )}
                                                            />
                                                            <Button
                                                                size="small"
                                                                icon={<DownOutlined />}
                                                                onClick={moveRowDown}
                                                                disabled={
                                                                    selectedRows.length !== 1 ||
                                                                    loading
                                                                }
                                                                title={t(
                                                                    'controlRoom.moveDown',
                                                                    'Move Down',
                                                                )}
                                                            />
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            }
                                            loading={loading}
                                        >
                                            <Table
                                                dataSource={financialItems}
                                                columns={sumatorColumns}
                                                size="small"
                                                pagination={false}
                                                rowKey="id"
                                                rowSelection={rowSelection}
                                                summary={sumatorSummary}
                                            />

                                            {/* Grand Totals moved from bottom section */}
                                            <div style={{ marginTop: 16 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong style={{ fontSize: '14px' }}>
                                                        <DollarOutlined
                                                            style={{
                                                                marginRight: 8,
                                                                color: '#52c41a',
                                                            }}
                                                        />
                                                        {t(
                                                            'controlRoom.grandTotalLei',
                                                            'GRAND TOTAL in LEI',
                                                        )}
                                                        :{' '}
                                                        {new Intl.NumberFormat('ro-RO', {
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        }).format(Math.round(totalSumator ?? 0))}
                                                    </Text>
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong style={{ fontSize: '14px' }}>
                                                        <EuroOutlined
                                                            style={{
                                                                marginRight: 8,
                                                                color: '#1890ff',
                                                            }}
                                                        />
                                                        {t(
                                                            'controlRoom.grandTotalEuro',
                                                            'GRAND TOTAL in EURO',
                                                        )}
                                                        :{' '}
                                                        {new Intl.NumberFormat('ro-RO', {
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        }).format(
                                                            Math.round(
                                                                totalSumator /
                                                                    (grandTotals.exchangeRate ?? 1),
                                                            ),
                                                        )}
                                                    </Text>
                                                </div>
                                                <div>
                                                    <Text strong style={{ fontSize: '14px' }}>
                                                        {t(
                                                            'controlRoom.exchangeRate',
                                                            'Exchange Rate',
                                                        )}
                                                        :{' '}
                                                        {new Intl.NumberFormat('ro-RO', {
                                                            maximumFractionDigits: 4,
                                                            minimumFractionDigits: 4,
                                                        }).format(grandTotals.exchangeRate ?? 0)}
                                                    </Text>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>

                                    {/* Middle Column - Financial Flow */}
                                    <Col xs={24} lg={8}>
                                        <Card
                                            title={withCurrency(
                                                t('controlRoom.financialFlow', 'Financial Flow'),
                                                'EUR',
                                                'LEI',
                                            )}
                                            loading={loading}
                                            size="small"
                                            style={{ height: 'fit-content' }}
                                        >
                                            <Row gutter={[8, 8]}>
                                                <Col span={6} />
                                                <Col span={6}>
                                                    <Text strong>
                                                        {t('controlRoom.euro', 'EURO')}
                                                    </Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>
                                                        {t('controlRoom.lei', 'LEI')}
                                                    </Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>
                                                        {t(
                                                            'controlRoom.contractCount',
                                                            'Contracts',
                                                        )}
                                                    </Text>
                                                </Col>
                                            </Row>
                                            {financialFlowRows.rows.map(row => (
                                                <Row
                                                    key={row.key}
                                                    gutter={[8, 8]}
                                                    style={{ marginTop: 4 }}
                                                    data-testid={`financial-flow-${row.key}`}
                                                >
                                                    <Col span={6}>
                                                        <Text>{t(`controlRoom.${row.key}`)}</Text>
                                                    </Col>
                                                    <Col span={6}>
                                                        <InputNumber
                                                            value={Math.round(row.euro)}
                                                            readOnly
                                                            size="small"
                                                            style={{ width: '100%' }}
                                                        />
                                                    </Col>
                                                    <Col span={6}>
                                                        <InputNumber
                                                            value={Math.round(row.lei)}
                                                            readOnly
                                                            size="small"
                                                            style={{ width: '100%' }}
                                                        />
                                                    </Col>
                                                    <Col span={6}>
                                                        <InputNumber
                                                            value={row.count}
                                                            placeholder="-"
                                                            readOnly
                                                            size="small"
                                                            style={{ width: '100%' }}
                                                            aria-label={t(
                                                                'controlRoom.contractCount',
                                                                'Contracts',
                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Row
                                                gutter={[8, 8]}
                                                style={{
                                                    marginTop: 8,
                                                    borderTop: '1px solid #d9d9d9',
                                                    paddingTop: 8,
                                                }}
                                                data-testid="financial-flow-total"
                                            >
                                                <Col span={6}>
                                                    <Text strong>
                                                        {t('controlRoom.total', 'Total')}
                                                    </Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>
                                                        {format0(financialFlowRows.total.euro)}
                                                    </Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>
                                                        {format0(financialFlowRows.total.lei)}
                                                    </Text>
                                                </Col>
                                                <Col span={6}>
                                                    <Text strong>
                                                        {financialFlowRows.total.count === null
                                                            ? '-'
                                                            : format0(
                                                                  financialFlowRows.total.count,
                                                              )}
                                                    </Text>
                                                </Col>
                                            </Row>
                                        </Card>

                                        {/* AU24K Breakdown - moved under Financial Flow */}
                                        <Card
                                            title={
                                                <Space size="small">
                                                    {withCurrency(
                                                        t(
                                                            'controlRoom.au24kBreakdown',
                                                            'AU24K Breakdown',
                                                        ),
                                                        'LEI',
                                                    )}
                                                    {au24kCalculated && (
                                                        <Tag
                                                            color="default"
                                                            style={{ marginInlineStart: 4 }}
                                                        >
                                                            {t(
                                                                'controlRoom.calculatedData',
                                                                'Calculated',
                                                            )}
                                                        </Tag>
                                                    )}
                                                </Space>
                                            }
                                            loading={loading}
                                            size="small"
                                            style={{ marginTop: 16 }}
                                        >
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 120px 120px',
                                                    gap: 8,
                                                    alignItems: 'center',
                                                    marginBottom: 8,
                                                    padding: '8px 0',
                                                    borderBottom: '2px solid #d9d9d9',
                                                }}
                                            >
                                                <Text strong>
                                                    {t('controlRoom.category', 'Category')}
                                                </Text>
                                                <Text strong>{t('controlRoom.gr', 'GR')}</Text>
                                                <Text strong>{t('controlRoom.lei', 'Lei')}</Text>
                                            </div>
                                            {au24kBreakdown.length === 0 ? (
                                                <Text>{t('controlRoom.noData', 'No data')}</Text>
                                            ) : (
                                                au24kBreakdown.map((row, index) => (
                                                    <div
                                                        key={row.category}
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr 120px 120px',
                                                            gap: 8,
                                                            padding: '8px 0',
                                                            alignItems: 'center',
                                                            backgroundColor: au24kCalculated
                                                                ? calculatedBackground
                                                                : undefined,
                                                            borderBottom:
                                                                index < au24kBreakdown.length - 1
                                                                    ? '1px solid #f0f0f0'
                                                                    : 'none',
                                                        }}
                                                    >
                                                        <Text>{row.category}</Text>
                                                        <Text strong>
                                                            {new Intl.NumberFormat('ro-RO', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            }).format(row.GR ?? 0)}
                                                        </Text>
                                                        <Text strong>
                                                            {new Intl.NumberFormat('ro-RO', {
                                                                maximumFractionDigits: 0,
                                                                minimumFractionDigits: 0,
                                                            }).format(Math.round(row.RON ?? 0))}
                                                        </Text>
                                                    </div>
                                                ))
                                            )}
                                        </Card>
                                    </Col>

                                    {/* Right Column - INDEX card */}
                                    <Col xs={24} lg={8}>
                                        <Card
                                            title={
                                                <Row
                                                    justify="space-between"
                                                    align="middle"
                                                    style={{ color: '#000000' }}
                                                >
                                                    <Col>
                                                        <span style={{ color: '#000000' }}>
                                                            <GoldOutlined
                                                                style={{
                                                                    marginRight: 8,
                                                                    color: '#faad14',
                                                                }}
                                                            />
                                                            {withCurrency(
                                                                t('controlRoom.goldIndex', 'INDEX'),
                                                                'LEI',
                                                            )}
                                                        </span>
                                                    </Col>
                                                    {indexCalculated && (
                                                        <Col>
                                                            <Tag color="default">
                                                                {t(
                                                                    'controlRoom.calculatedData',
                                                                    'Calculated',
                                                                )}
                                                            </Tag>
                                                        </Col>
                                                    )}
                                                </Row>
                                            }
                                            loading={loading}
                                            size="small"
                                            style={{ display: 'flex', flexDirection: 'column' }}
                                        >
                                            {/* Reference values input section */}
                                            <div
                                                style={{
                                                    marginBottom: 12,
                                                    padding: '8px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                <Text
                                                    strong
                                                    style={{
                                                        fontSize: '12px',
                                                        marginBottom: '4px',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {t(
                                                        'controlRoom.referenceValues',
                                                        'Reference Values',
                                                    )}
                                                    :
                                                </Text>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        flexWrap: 'nowrap',
                                                    }}
                                                >
                                                    <Text
                                                        style={{
                                                            fontSize: '11px',
                                                            width: 72,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        % Rosu &lt;
                                                    </Text>
                                                    <InputNumber
                                                        value={seeP1}
                                                        onChange={value => setSeeP1(value ?? 0)}
                                                        size="small"
                                                        style={{ width: 72 }}
                                                        precision={0}
                                                        disabled={referencesLoading}
                                                    />
                                                    <Text
                                                        style={{
                                                            fontSize: '11px',
                                                            width: 88,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        % Galben &lt;
                                                    </Text>
                                                    <InputNumber
                                                        value={seeP2}
                                                        onChange={value => setSeeP2(value ?? 0)}
                                                        size="small"
                                                        style={{ width: 72 }}
                                                        precision={0}
                                                        disabled={referencesLoading}
                                                    />
                                                    <Text
                                                        style={{
                                                            fontSize: '11px',
                                                            width: 60,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        % Verde
                                                    </Text>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        loading={referencesLoading}
                                                        onClick={() =>
                                                            saveReferenceValues(seeP1, seeP2)
                                                        }
                                                        style={{
                                                            fontSize: '10px',
                                                            padding: '0 16px',
                                                            marginLeft: 'auto',
                                                        }}
                                                    >
                                                        {t('controlRoom.save', 'Salveaza')}
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Table-like index display */}
                                            <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 80px 80px',
                                                        gap: 2,
                                                        fontWeight: 'bold',
                                                        fontSize: '11px',
                                                        marginBottom: 4,
                                                        padding: '4px',
                                                        backgroundColor: '#f0f0f0',
                                                        borderRadius: '2px',
                                                    }}
                                                >
                                                    <div>
                                                        {t(
                                                            'controlRoom.description',
                                                            'Description',
                                                        )}
                                                    </div>
                                                    <div style={{ textAlign: 'center' }}>
                                                        {t('controlRoom.value', 'Value')}
                                                    </div>
                                                    <div style={{ textAlign: 'center' }}>
                                                        {t('controlRoom.index', 'Index')}
                                                    </div>
                                                </div>
                                                {financeGroupsData.length === 0 ? (
                                                    <div
                                                        style={{
                                                            padding: '8px',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <Text style={{ fontSize: '11px' }}>
                                                            {t('controlRoom.noData', 'No data')}
                                                        </Text>
                                                    </div>
                                                ) : (
                                                    financeGroupsData.map(group => {
                                                        const draftIndex =
                                                            financeGroupIndexDrafts[group.id] ??
                                                            Math.round(group.indexBaza ?? 0);
                                                        const indexChanged =
                                                            draftIndex !==
                                                            Math.round(group.indexBaza ?? 0);
                                                        const thresholdColor = getRowColor(
                                                            group.valoare ?? 0,
                                                            group.indexBaza ?? 0,
                                                        );
                                                        // When the row has no threshold highlight (white) and the
                                                        // data is reconstructed for a historical date, fall back to
                                                        // silver to mirror the Delphi clSilver grid indicator.
                                                        const rowColor =
                                                            thresholdColor === '#ffffff' &&
                                                            indexCalculated
                                                                ? calculatedBackground
                                                                : thresholdColor;

                                                        return (
                                                            <div
                                                                key={group.id}
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns:
                                                                        '1fr 80px 116px',
                                                                    gap: 2,
                                                                    padding: '3px 4px',
                                                                    fontSize: '11px',
                                                                    backgroundColor: rowColor,
                                                                    marginBottom: 1,
                                                                    borderRadius: '2px',
                                                                    border: '1px solid #d9d9d9',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight:
                                                                            group.denumire.includes(
                                                                                'TOTAL',
                                                                            )
                                                                                ? 'bold'
                                                                                : 'normal',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {group.denumire}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'flex-end',
                                                                        gap: 4,
                                                                        fontWeight:
                                                                            group.denumire.includes(
                                                                                'TOTAL',
                                                                            )
                                                                                ? 'bold'
                                                                                : 'normal',
                                                                        color: '#000000',
                                                                    }}
                                                                >
                                                                    {Math.round(group.valoare ?? 0)}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        textAlign: 'right',
                                                                        fontWeight:
                                                                            group.denumire.includes(
                                                                                'TOTAL',
                                                                            )
                                                                                ? 'bold'
                                                                                : 'normal',
                                                                        color: '#000000',
                                                                    }}
                                                                >
                                                                    <InputNumber
                                                                        aria-label={t(
                                                                            'controlRoom.index',
                                                                            'Index',
                                                                        )}
                                                                        value={draftIndex}
                                                                        min={0}
                                                                        precision={0}
                                                                        size="small"
                                                                        style={{ width: 68 }}
                                                                        disabled={loading}
                                                                        onChange={value =>
                                                                            setFinanceGroupIndexDrafts(
                                                                                previous => ({
                                                                                    ...previous,
                                                                                    [group.id]:
                                                                                        value ?? 0,
                                                                                }),
                                                                            )
                                                                        }
                                                                    />
                                                                    <Button
                                                                        size="small"
                                                                        icon={<SaveOutlined />}
                                                                        disabled={
                                                                            !indexChanged || loading
                                                                        }
                                                                        loading={
                                                                            financeGroupSavingId ===
                                                                            group.id
                                                                        }
                                                                        onClick={() =>
                                                                            handleSaveFinanceGroupIndex(
                                                                                group,
                                                                            )
                                                                        }
                                                                        title={t(
                                                                            'controlRoom.saveIndex',
                                                                            'Save index',
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Bottom Section */}
                                <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                                    {/* Notes */}
                                    <Col xs={24} lg={12}>
                                        <Card
                                            title={withCurrency(
                                                t('controlRoom.notes', 'NOTES'),
                                                'LEI',
                                            )}
                                            loading={loading}
                                            style={{ height: '300px' }}
                                        >
                                            <Space.Compact
                                                style={{ width: '100%', marginBottom: 8 }}
                                            >
                                                <Input
                                                    value={financeNoteDenumire}
                                                    onChange={event =>
                                                        setFinanceNoteDenumire(event.target.value)
                                                    }
                                                    placeholder={t(
                                                        'controlRoom.description',
                                                        'Description',
                                                    )}
                                                    maxLength={25}
                                                />
                                                <InputNumber
                                                    value={financeNoteSuma}
                                                    onChange={value => setFinanceNoteSuma(value)}
                                                    placeholder={t('controlRoom.amount', 'Amount')}
                                                    style={{ width: 120 }}
                                                />
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    loading={financeNoteSaving}
                                                    onClick={handleAddFinanceNote}
                                                    title={t('controlRoom.addNote', 'Add note')}
                                                />
                                            </Space.Compact>
                                            <Table
                                                dataSource={financeNotes}
                                                columns={notesColumns}
                                                size="small"
                                                pagination={false}
                                                scroll={{ y: 156 }}
                                                rowKey="id"
                                            />
                                        </Card>
                                    </Col>

                                    {/* Delivered Not Invoiced */}
                                    <Col xs={24} lg={12}>
                                        <Card
                                            title={t(
                                                'controlRoom.deliveredNotInvoiced',
                                                'Delivered Not Invoiced',
                                            )}
                                            loading={loading}
                                            style={{ height: '300px' }}
                                        >
                                            <Table
                                                dataSource={deliveredNotInvoiced}
                                                columns={deliveredNotInvoicedColumns}
                                                size="small"
                                                pagination={false}
                                                scroll={{ y: 200 }}
                                                rowKey="id"
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        ),
                    },
                    {
                        key: 'approvals',
                        label: t('controlRoom.tabs.approvals', 'Postponement Approvals'),
                        children: <PostponementApprovalsTab />,
                    },
                    {
                        key: 'controlling',
                        label: t('controlRoom.tabs.controlling', 'Controlling'),
                        children: <ControllingTab />,
                    },
                    {
                        key: 'authorizedOps',
                        label: t('controlRoom.tabs.authorizedOps', 'Operatii autorizate'),
                        children: (
                            <Row gutter={[32, 0]}>
                                <Col xs={24} md={12}>
                                    <Card size="small">
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Text strong>
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.authorizedOps.bancaToSeif',
                                                        'Suma BANCA -> SEIF',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <InputNumber
                                                value={bancaToSeifAmount}
                                                onChange={value => setBancaToSeifAmount(value)}
                                                min={0.01}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                data-testid="banca-to-seif-amount"
                                            />
                                            <Checkbox
                                                checked={bancaToSeifRc}
                                                onChange={e => setBancaToSeifRc(e.target.checked)}
                                                data-testid="banca-to-seif-rc"
                                            >
                                                {t(
                                                    'controlRoom.authorizedOps.includeInRC',
                                                    'Include in RC (Registru de casa)',
                                                )}
                                            </Checkbox>
                                            <Button
                                                type="primary"
                                                loading={authorizedOpsSaving}
                                                onClick={() => void handleBancaToSeif()}
                                                data-testid="banca-to-seif-button"
                                            >
                                                {t(
                                                    'controlRoom.authorizedOps.bancaToSeifButton',
                                                    'Ridicare numerar (BANCA->SEIF)',
                                                )}
                                            </Button>
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Card size="small">
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Text strong>
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.authorizedOps.seifToBanca',
                                                        'Suma SEIF -> BANCA',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <InputNumber
                                                value={seifToBancaAmount}
                                                onChange={value => setSeifToBancaAmount(value)}
                                                min={0.01}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                data-testid="seif-to-banca-amount"
                                            />
                                            <Checkbox
                                                checked={seifToBancaRc}
                                                onChange={e => setSeifToBancaRc(e.target.checked)}
                                                data-testid="seif-to-banca-rc"
                                            >
                                                {t(
                                                    'controlRoom.authorizedOps.includeInRC',
                                                    'Include in RC (Registru de casa)',
                                                )}
                                            </Checkbox>
                                            <Button
                                                type="primary"
                                                loading={authorizedOpsSaving}
                                                onClick={() => void handleSeifToBanca()}
                                                data-testid="seif-to-banca-button"
                                            >
                                                {t(
                                                    'controlRoom.authorizedOps.seifToBancaButton',
                                                    'Depunere numerar (SEIF -> BANCA)',
                                                )}
                                            </Button>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        ),
                    },
                    {
                        key: 'dailyMovements',
                        label: t('controlRoom.tabs.dailyMovements', 'Daily Movements'),
                        children: (
                            <Card
                                loading={loading}
                                size="small"
                                title={withCurrency(
                                    t('controlRoom.tabs.dailyMovements', 'Daily Movements'),
                                    'LEI',
                                )}
                            >
                                <Table
                                    dataSource={dailyMovements}
                                    rowKey={record => `${record.type}-${record.group}`}
                                    rowClassName="daily-movements-group-row"
                                    columns={dailyMovementsGroupColumns}
                                    size="small"
                                    pagination={false}
                                    expandable={{
                                        defaultExpandAllRows: true,
                                        expandedRowRender: renderMovementsEntryTable,
                                    }}
                                />
                            </Card>
                        ),
                    },
                    {
                        key: 'dailyCashCheck',
                        label: t('controlRoom.tabs.dailyCashCheck', 'Daily Cash Check'),
                        children: (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Row gutter={[12, 12]}>
                                    <Col xs={24} md={6}>
                                        <Card size="small" loading={loading}>
                                            <Text type="secondary">
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.dailyCashCheck.expected',
                                                        'Expected',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <Title level={4} style={{ margin: 0 }}>
                                                {format0(dailyCashCheck?.expected ?? 0)}
                                            </Title>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card size="small" loading={loading}>
                                            <Text type="secondary">
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.dailyCashCheck.actual',
                                                        'Actual',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <Title level={4} style={{ margin: 0 }}>
                                                {formatCounted(dailyCashCheck?.actual)}
                                            </Title>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card size="small" loading={loading}>
                                            <Text type="secondary">
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.dailyCashCheck.difference',
                                                        'Difference',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <Title level={4} style={{ margin: 0 }}>
                                                <Tag
                                                    color={dailyCashDifferenceColor}
                                                    data-testid="daily-cash-check-difference"
                                                >
                                                    {formatCounted(dailyCashCheck?.difference)}
                                                </Tag>
                                            </Title>
                                            <Text type="secondary">
                                                {t(
                                                    `controlRoom.dailyCashCheck.${dailyCashDifferenceState}`,
                                                    dailyCashDifferenceState,
                                                )}
                                            </Text>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card size="small" loading={loading}>
                                            <Text type="secondary">
                                                {withCurrency(
                                                    t(
                                                        'controlRoom.dailyCashCheck.previousBalance',
                                                        'Previous balance',
                                                    ),
                                                    'LEI',
                                                )}
                                            </Text>
                                            <Title level={4} style={{ margin: 0 }}>
                                                {format0(dailyCashCheck?.previousBalance ?? 0)}
                                            </Title>
                                        </Card>
                                    </Col>
                                </Row>
                                <Card
                                    loading={loading}
                                    size="small"
                                    title={withCurrency(
                                        t('controlRoom.dailyCashCheck.breakdown', 'Breakdown'),
                                        'LEI',
                                    )}
                                >
                                    <Table
                                        dataSource={dailyCashBreakdown}
                                        rowKey={record => `${record.type}-${record.group}`}
                                        rowClassName="daily-cash-check-group-row"
                                        columns={dailyMovementsGroupColumns}
                                        size="small"
                                        pagination={false}
                                        expandable={{
                                            defaultExpandAllRows: true,
                                            expandedRowRender: renderMovementsEntryTable,
                                        }}
                                    />
                                </Card>
                            </Space>
                        ),
                    },
                    // The operation log is for the owner and the administrators only (D2).
                    ...(isAdmin
                        ? [
                              {
                                  key: 'auditLog',
                                  label: t('controlRoom.tabs.auditLog', 'User Operations'),
                                  children: <AuditLogPanel openings={auditLogOpenings} />,
                              },
                          ]
                        : []),
                ]}
            />

            <Modal
                title={t('controlRoom.addLine', 'Add Line')}
                open={addLineModalOpen}
                onOk={handleAddLine}
                onCancel={() => setAddLineModalOpen(false)}
                confirmLoading={addLineSaving}
                okText={t('controlRoom.save', 'Salveaza')}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text>{t('controlRoom.lineName', 'Line Name')}</Text>
                        <Input
                            value={addLineDenumire}
                            onChange={e => setAddLineDenumire(e.target.value)}
                            maxLength={50}
                        />
                    </div>
                    <div>
                        <Text>{t('controlRoom.deltaAmount', 'Amount (+/-)')}</Text>
                        <InputNumber
                            value={addLineSuma}
                            onChange={value => setAddLineSuma(value)}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div>
                        <Text>{t('controlRoom.reprezentand', 'Reason')}</Text>
                        <Input
                            value={addLineReprezentand}
                            onChange={e => setAddLineReprezentand(e.target.value)}
                            maxLength={255}
                        />
                    </div>
                </Space>
            </Modal>

            <Modal
                title={t('controlRoom.modifyLine', 'Modify Line')}
                open={modifyLineModalOpen}
                onOk={handleModifyLine}
                onCancel={() => setModifyLineModalOpen(false)}
                confirmLoading={modifyLineSaving}
                okText={t('controlRoom.save', 'Salveaza')}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text>{t('controlRoom.deltaAmount', 'Amount (+/-)')}</Text>
                        <InputNumber
                            value={modifyLineDelta}
                            onChange={value => setModifyLineDelta(value)}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div>
                        <Text>{t('controlRoom.reprezentand', 'Reason')}</Text>
                        <Input
                            value={modifyLineReprezentand}
                            onChange={e => setModifyLineReprezentand(e.target.value)}
                            maxLength={255}
                        />
                    </div>
                </Space>
            </Modal>
        </div>
    );
};

export default ControlRoom;
